import {
  ApolloClient,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import { sha256 } from "hash.js";
import { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";

type Props = {
  token: string | null;
  graphqlEndpoint: string;
};

export const useApollo = ({ token, graphqlEndpoint }: Props) => {
  const [cookie, setCookies, removeCookies] = useCookies(["authToken"]);

  const authLink = (token: string | null) => {
    return setContext(async (_, { headers }) => {
      if (token) {
        return {
          headers: {
            ...headers,
            authorization: `Bearer ${token}`,
          },
        };
      }
      return { headers };
    });
  };

  const retryLink = useMemo(() => {
    return new RetryLink({
      delay: {
        initial: 1000,
        max: 3,
        jitter: true,
      },
      attempts: {
        max: 2,
        retryIf: (error, _operation) => {
          if (
            _operation.operationName === "signin" ||
            _operation.operationName === "register"
          ) {
            return false;
          }
          return !!error;
        },
      },
    });
  }, []);

  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

  const currentTime = Date.now();
  const [futureDate, setFutureDate] = useState<number>();

  if (futureDate && currentTime > futureDate) {
    client?.resetStore();
    setFutureDate(Date.now() + 10 * 1000 * 60);
  }

  useEffect(() => {
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (
        networkError &&
        "statusCode" in networkError &&
        networkError.statusCode === 401
      ) {
        removeCookies("authToken", { path: "/" });
      }
    });

    async function init() {
      const cache = new InMemoryCache({
        dataIdFromObject(responseObject) {
          const jsonString = JSON.stringify(responseObject);
          const hash = sha256().update(jsonString).digest("hex");
          return `${responseObject.__typename}:${hash}`;
        },
      });

      setClient(
        new ApolloClient({
          cache,
          link: from([
            errorLink,
            retryLink,
            authLink(token),
            createUploadLink({
              uri: graphqlEndpoint || "http://localhost:1337/graphql",
              headers: {
                "Apollo-Require-Preflight": "true",
              },
            }) as any,
          ]),
          connectToDevTools: process.env["NODE_ENV"] === "development",
          defaultOptions: {
            watchQuery: {
              fetchPolicy: "cache-and-network",
            },
          },
        })
      );
      setFutureDate(Date.now() + 10 * 1000 * 60);
    }

    init().catch(console.error);
  }, [graphqlEndpoint, retryLink, token, removeCookies]);

  return client;
};
